/**
 * I8 fishing bite: cast, a random wait, a bite tell (bobber dip), then a 0.3 s strike window.
 * A strike in the window lands the fish table of the fish-spot (data/professions.ts); one in the window's first
 * 0.1 s is "perfect" and doubles the catch. Early strike or a late one is a miss. All randomness from `h.random()`.
 * The client calls `cast()` / `strike()` on `world.sys.fishBite` and listens to the `fishBite` events.
 */
import type { SystemHost, WorldSystem } from './types';
import type { WorldEvent } from '../world';
import { NODE_KINDS } from '../data/professions';

export const FISH_BITE = { waitMin: 2, waitMax: 6, window: 0.3, perfect: 0.1 } as const;
export type FishBitePhase = 'idle' | 'wait' | 'tell';
export type FishBiteResult = 'catch' | 'perfect' | 'miss';

/** Commands (part of `PlayerCommand`). */
export type FishBiteCommand = { t: 'fishCast' } | { t: 'fishStrike' };
/** Events (part of `WorldEvent`). */
export type FishBiteEvent =
  | { type: 'fishBite'; phase: 'cast' | 'tell' }
  | { type: 'fishBite'; phase: 'result'; result: FishBiteResult; goods?: { id: string; n: number }[] };

export class FishBiteSystem implements WorldSystem {
  readonly name = 'fishBite';
  phase: FishBitePhase = 'idle';
  private t = 0;
  private wait = 0;
  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    fishCast: () => {
      if (this.h.world.trades.nodeNear(this.h.world.player)?.kind.id === 'fish-spot') this.cast();
    },
    fishStrike: () => {
      this.strike();
    },
  };

  private ev(e: Record<string, unknown>): void {
    this.h.emit({ type: 'fishBite', ...e } as WorldEvent);
  }

  cast(): boolean {
    if (this.phase !== 'idle') return false;
    this.phase = 'wait';
    this.t = 0;
    this.wait = FISH_BITE.waitMin + this.h.random() * (FISH_BITE.waitMax - FISH_BITE.waitMin);
    this.ev({ phase: 'cast' });
    return true;
  }

  strike(): FishBiteResult | null {
    if (this.phase === 'idle') return null;
    const inWindow = this.phase === 'tell';
    const perfect = inWindow && this.t < FISH_BITE.perfect;
    this.phase = 'idle';
    if (!inWindow) {
      this.ev({ phase: 'result', result: 'miss' });
      return 'miss';
    }
    const goods: { id: string; n: number }[] = [];
    for (const y of NODE_KINDS['fish-spot']!.yields) {
      if (y.chance < 1 && this.h.random() >= y.chance) continue;
      const n = y.min + Math.floor(this.h.random() * (y.max - y.min + 1));
      goods.push({ id: y.id, n: perfect ? n * 2 : n });
    }
    for (const g of goods) this.h.world.trades.give(g.id, g.n);
    const result: FishBiteResult = perfect ? 'perfect' : 'catch';
    this.ev({ phase: 'result', result, goods });
    return result;
  }

  step(dt: number): void {
    if (this.phase === 'idle') return;
    this.t += dt;
    if (this.phase === 'wait' && this.t >= this.wait) {
      this.phase = 'tell';
      this.t = 0;
      this.ev({ phase: 'tell' });
    } else if (this.phase === 'tell' && this.t >= FISH_BITE.window) {
      this.phase = 'idle';
      this.ev({ phase: 'result', result: 'miss' });
    }
  }
}
